import React, { useContext, useMemo } from 'react'
import { Grid, GridCell, Text, View, useWindowResizeSensor } from 'oio-react'
import PropTypes from 'prop-types'
import { withSize } from 'react-sizeme'
import ClockIcon from 'assets/icons/clock'
import LocationIcon from 'assets/icons/location'
import PrivacyIcon from 'assets/icons/privacy'
import { arrayToSentenceFragment } from 'src/components/utils'
import { InitiativeHierarchyContext } from 'src/libraries/contexts/InitiativeHierarchy'

// =======================================================
// Component
// =======================================================

const InitiativeDetailsBlock = ({ showLocation, size }) => {
   const { initiative } = useContext(InitiativeHierarchyContext)

   // Use react-sizeme HOC to determine block's current width
   const blockWidth = size.width

   // By default, content flows from left to right
   // (we are using flexbox naming conventions here)
   let blockContentFlow = 'row'

   // Set flexFlow to be column if this block is on mobile or
   // if block width is less than 500 at larger OIO breakpoint sizes
   const { currentBreakpoint } = useWindowResizeSensor()
   if (currentBreakpoint < 'b' || blockWidth < 550) {
      blockContentFlow = 'column'
   }

   const [publicPrivilegeStatement, communityMemberPrivilegeStatement] = useMemo(() => {
      // Little util to help us
      const getRolePolicy = (roleType, [roleOwnerFilterKey, roleOwnerFilterValue]) => (
         initiative.rolePolicies.find(policy => (
            policy.role.type === roleType &&
               policy.roleOwner[roleOwnerFilterKey] === roleOwnerFilterValue
         ))
      )

      // The various role policies we are concerned with
      const communityPrimaryRolePolicy = getRolePolicy('primary', ['__typename', 'Organization'])
      const publicRolePolicy = getRolePolicy('public', ['__typename', 'Organization'])

      // The privilege keys themselves
      const spks = policy => policy.privileges
         .map(p => p.spk)
         .filter(spk => ['join', 'view'].includes(spk))
         .map(spk => (spk === 'join' ? initiative.joinVerb : spk))

      const communityPrimaryRolePolicyPrivilegeSPKs = spks(communityPrimaryRolePolicy)
      const publicRolePolicyPrivilegeSPKs = spks(publicRolePolicy)

      const computedPublicPrivilegeStatement = publicRolePolicy.privileges.length
         ? `Any public user can ${arrayToSentenceFragment(publicRolePolicyPrivilegeSPKs)}
            this ${initiative.type.nameSingular}.`
         : `This ${initiative.type.nameSingular} is not visible to the public.`

      const computedCommunityMemberPrivilegeStatement = communityPrimaryRolePolicy.privileges.length
         ? `Any Community Member can
            ${arrayToSentenceFragment(communityPrimaryRolePolicyPrivilegeSPKs)}
            this ${initiative.type.nameSingular}.`
         : `Only Community Members that are invited can see and ${initiative.joinVerb} this
            ${initiative.type.nameSingular}.`

      return [
         computedPublicPrivilegeStatement,
         computedCommunityMemberPrivilegeStatement
      ]
   }, [initiative])

   return (
      <View
         display="flex"
         flexFlow={blockContentFlow}
         width="100%"
         backgroundColor="#f3f3f3"
         borderRadius="6px"
         padding="24px">
         <View
            flex="0 0 auto"
            float="left"
            width={blockContentFlow === 'row' ? '132px' : '100%'}>
            <Text size="1.5" weight="bold">
               {`${initiative.type.nameSingular} Details`}
            </Text>
         </View>
         <View
            float="left"
            paddingLeft={blockContentFlow === 'row' && '36px'}
            marginTop={blockContentFlow === 'column' && '12px'}
            paddingTop={blockContentFlow === 'column' && '12px'}
            borderTop={blockContentFlow === 'column' && '1px solid #ddd'}>
            <Text
               color="#666"
               size="2"
               style={{ letterSpacing: '-0.1px' }}
               sizeMultiplier="0.95"
               weight="normal">
               <Grid columns="1" spacing="9px">
                  {initiative.class === 'event' && (
                     <GridCell>
                        <View
                           float="left"
                           width="100%"
                           display="flex"
                           padding="0px 0px 9px 0px"
                           borderBottom="1px solid #ddd"
                           alignItems="center">
                           <View
                              float="left"
                              width="20px"
                              height="20px"
                              flex="0 0 auto"
                              display="flex"
                              justifyContent="center"
                              alignItems="center"
                              marginLeft="6px"
                              marginRight="24px">
                              <ClockIcon
                                 color="#666"
                                 width="16px"
                                 height="16px"
                              />
                           </View>
                           <View>
                              <Text size="1.5" color="#666">
                                 {initiative.startDateTime
                                    ? initiative.startDateTime
                                    : 'Date and time has not yet been set'
                                 }
                              </Text>
                              {initiative.endDateTime && (
                                 <Text size="1.5" color="#666">
                                    {`to ${initiative.endDateTime}`}
                                 </Text>
                              )}
                           </View>
                        </View>
                     </GridCell>
                  )}
                  {showLocation && initiative.location.formattedAddress && (
                     <GridCell>
                        <View
                           float="left"
                           width="100%"
                           display="flex"
                           alignItems="center"
                           padding="0px 0px 9px 0px"
                           borderBottom="1px solid #ddd">
                           <View
                              float="left"
                              width="20px"
                              height="20px"
                              flex="0 0 auto"
                              display="flex"
                              justifyContent="center"
                              alignItems="center"
                              marginLeft="6px"
                              marginRight="24px">
                              <LocationIcon
                                 color="#666"
                                 width="18px"
                                 height="18px"
                              />
                           </View>
                           <View>
                              <Text size="1.5" color="#666">
                                 {initiative.location.formattedAddress}
                                 &nbsp;
                                 <a
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(initiative.location.formattedAddress)}`}>
                                    <Text size="1.5" color="#888">
                                       <u>View Map</u>
                                    </Text>
                                 </a>
                              </Text>
                           </View>
                        </View>
                     </GridCell>
                  )}
                  {/* This is for Sessions */}
                  {initiative.ancestors.length > 0 && initiative.location.name && (
                     <GridCell>
                        <View
                           float="left"
                           width="100%"
                           display="flex"
                           alignItems="center">
                           <View
                              float="left"
                              width="20px"
                              height="20px"
                              flex="0 0 auto"
                              display="flex"
                              justifyContent="center"
                              alignItems="center"
                              marginLeft="6px"
                              marginRight="24px">
                              <LocationIcon
                                 color="#666"
                                 width="18px"
                                 height="18px"
                              />
                           </View>
                           <View>
                              <Text size="2" color="#666">
                                 {initiative.location.name}
                              </Text>
                           </View>
                        </View>
                     </GridCell>
                  )}
                  {initiative.ancestors.length === 0 && (
                     <GridCell>
                        <View
                           float="left"
                           width="100%"
                           display="flex"
                           alignItems="center">
                           <View
                              float="left"
                              width="20px"
                              height="20px"
                              flex="0 0 auto"
                              display="flex"
                              justifyContent="center"
                              alignItems="center"
                              marginLeft="6px"
                              marginRight="24px">
                              <PrivacyIcon
                                 color="#666"
                                 width="18px"
                                 height="18px"
                              />
                           </View>
                           <View>
                              <Text size="1.5" color="#666">
                                 {publicPrivilegeStatement}
                                 <br />
                                 {communityMemberPrivilegeStatement}
                              </Text>
                           </View>
                        </View>
                     </GridCell>
                  )}
               </Grid>
            </Text>
         </View>
      </View>
   )
}

// =======================================================
// Props + Decorators
// =======================================================

InitiativeDetailsBlock.propTypes = {
   showLocation: PropTypes.bool,
   size: PropTypes.object.isRequired
}

InitiativeDetailsBlock.defaultProps = {
   showLocation: true
}

export default withSize()(InitiativeDetailsBlock)
